import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { customAxiosApi, globalConfig } from "src/config/axios-retry";
import Toast from "src/shared/toast/Toast";
import { CvFiltersEnumObject, CvSearchFiltersInterface, CvSearchInputsInterface, CvSearchListApiRequestInterface, CvSearchListApiResponseInterface, CvSearchStoreInterface } from "src/utils/interfaces/cv-search/CvSearchInterfaces";
import { CvSearchProfileInterface } from "src/utils/interfaces/cv-search/CvSearchProfileInterface";
import { getPartnerInfo } from "../partner/partner";
import { t } from "i18next";
import { getApiResponseFromCache, invalidateApiResponseInCache, setApiResponseInCache, updateObjectInList } from "src/utils/helpers/utils";
import { CV_SEARCH_PAGE_LIMIT } from "src/utils/constants/constants";
import { buildCvSearchFiltersLabel } from "src/utils/google-analytics/marketing-helpers";
import { getPartnerMarketingContext, trackCvDatabaseSearched } from "src/utils/google-analytics/marketing";
import { RootState } from "../store";

const appKey = 'cvSearch';
const apiModuleUrl = 'pa/ft/cv-search';
const listCvsUrl = 'pa/ft/cv-search/list';

const trackCvSearchIfNeeded = (
    query: CvSearchListApiRequestInterface,
    res: CvSearchListApiResponseInterface & { fromCache?: boolean },
    getState: () => RootState,
) => {
    if (query?.skip === 0 && res?.total != null) {
        const { partner, partnerUser } = getPartnerMarketingContext();
        const searchInputs = getState().cvSearch.searchInputs;
        const jobTitle = searchInputs?.jobTitle?.displayName;
        const jobLocation = searchInputs?.jobLocation?.displayName;
        const searchQuery = [jobTitle, jobLocation].filter(Boolean).join(' | ');

        trackCvDatabaseSearched({
            partner,
            partnerUser,
            searchQuery,
            filtersApplied: buildCvSearchFiltersLabel(query?.filters),
            resultsCount: res.total,
        });
    }
};

export const searchCvs = createAsyncThunk(`${appKey}/searchCvs`, async (query: CvSearchListApiRequestInterface, { dispatch, getState }) => {
    const queryParams = { ...query, ...query.filters }; delete queryParams.filters;
    const cacheData: CvSearchListApiResponseInterface = getApiResponseFromCache(listCvsUrl, queryParams);
    if (cacheData) {
        dispatch(prefetchNextCvsPageIfNeeded({ responseData: cacheData, queryParams }));
        return new Promise((resolve: any) => {
            setTimeout(() => {
                const result = { ...cacheData, fromCache: true };
                trackCvSearchIfNeeded(query, result, getState);
                resolve(result);
            }, 200);
        });
    }

    const response = await customAxiosApi.get(listCvsUrl, { ...globalConfig, params: queryParams });
    const responseData: CvSearchListApiResponseInterface = response.data.data;
    setApiResponseInCache(listCvsUrl, queryParams, responseData);

    dispatch(prefetchNextCvsPageIfNeeded({ responseData, queryParams }));

    const result = { ...responseData, fromCache: false };
    trackCvSearchIfNeeded(query, result, getState);
    return result;
})

export const prefetchNextCvsPageIfNeeded = createAsyncThunk(`${appKey}/prefetchNextCvsPageIfNeeded`, async (params: { responseData: CvSearchListApiResponseInterface, queryParams: CvSearchListApiRequestInterface }) => {
    const { responseData, queryParams } = params;
    const newQueryParams = { ...queryParams, skip: queryParams.skip + CV_SEARCH_PAGE_LIMIT };
    const cacheData = getApiResponseFromCache(listCvsUrl, newQueryParams);

    if (responseData?.total > CV_SEARCH_PAGE_LIMIT && !cacheData) {
        const response = await customAxiosApi.get(listCvsUrl, { ...globalConfig, params: newQueryParams });
        const responseData2: CvSearchListApiResponseInterface = response.data.data;
        setApiResponseInCache(listCvsUrl, newQueryParams, responseData2);
        return { ...responseData2, fromCache: false }
    }

    return { viewsRemaining: cacheData?.viewsRemaining || null, fromCache: true };
})

export const countCvs = createAsyncThunk(`${appKey}/countCvs`, async (query?: { locationId: string, jobTitleId: string }) => {
    const response = await customAxiosApi.get(`${listCvsUrl}?skip=0&limit=5&locationId=${query.locationId}&jobTitleId=${query.jobTitleId}`, { ...globalConfig });
    return response.data.data?.total;
})

export const getCvProfileDetails = createAsyncThunk(`${appKey}/getCvProfileDetails`, async (query: { profileId: string, queryParams: CvSearchListApiRequestInterface }) => {
    const response = await customAxiosApi.get(`${apiModuleUrl}/${query.profileId}/details`, { ...globalConfig, params: query.queryParams });

    const resData = response.data.data;
    return { ...resData, canViewDetails: true };
})

export const unlockCvProfile = createAsyncThunk(`${appKey}/unlockCvProfile`, async (query: { profileId: string }, { dispatch }) => {
    const response = await customAxiosApi.put(`${apiModuleUrl}/${query.profileId}/unlock`, {}, globalConfig);
    if (response.status === 200) {
        dispatch(getPartnerInfo());
    }
    return response.data.data;
})

export const getCvFavoriteProfiles = createAsyncThunk(`${appKey}/getCvFavoriteProfiles`, async (query?: { skip: number, limit: number }) => {
    const response = await customAxiosApi.get(`${apiModuleUrl}/list?favoritesOnly=true&skip=${query.skip}&limit=${query.limit}`, globalConfig);
    return response.data.data;
})

export const addCvProfileToFavorites = createAsyncThunk(`${appKey}/addCvProfileToFavorites`, async (query: { profileId: string, queryParams: CvSearchListApiRequestInterface }) => {
    const response = await customAxiosApi.post(`${apiModuleUrl}/${query.profileId}/favorite`, {}, { ...globalConfig, params: query.queryParams });
    return response.data.data;
})

export const removeCvProfileFromFavorites = createAsyncThunk(`${appKey}/removeCvProfileFromFavorites`, async (query: { profileId: string, queryParams: CvSearchListApiRequestInterface }) => {
    const response = await customAxiosApi.delete(`${apiModuleUrl}/${query.profileId}/favorite`, { ...globalConfig, params: query.queryParams });
    return response.data.data;
})

export const reportCvProfile = createAsyncThunk(`${appKey}/reportCvProfile`, async (query?: { skip: number, limit: number }) => {
    const response = await customAxiosApi.post(`${apiModuleUrl}?skip=${query.skip}&limit=${query.limit}`, { body: '' }, globalConfig);
    return response.data.data;
})

const getInvalidationParams = (state: CvSearchStoreInterface) => {
    return {
        skip: (state.currentPage - 1) * CV_SEARCH_PAGE_LIMIT,
        limit: CV_SEARCH_PAGE_LIMIT,
        jobTitleId: state.searchInputs?.jobTitle?.objectID,
        locationId: state.searchInputs?.jobLocation?.objectID,
        ...state.filters
    }
}

export const setCvSearchInputs = createAction<CvSearchInputsInterface>(`${appKey}/setCvSearchInputs`);
export const setIsFiltersMenuVisible = createAction<boolean>(`${appKey}/setIsFiltersMenuVisible`);
export const setSelectedProfile = createAction<CvSearchProfileInterface>(`${appKey}/setSelectedProfile`);
export const setCurrentPage = createAction<number>(`${appKey}/setCurrentPage`);
export const setCurrentFavoritesPage = createAction<number>(`${appKey}/setCurrentFavoritesPage`);
export const setIsChatView = createAction<boolean>(`${appKey}/setIsChatView`);
export const setCvSearchFilters = createAction<CvSearchFiltersInterface>(`${appKey}/setCvSearchFilters`);
export const setIsFiltersReset = createAction<boolean>(`${appKey}/setIsFiltersReset`);
export const setShowFairUsageModal = createAction<boolean>(`${appKey}/setShowFairUsageModal`);
export const setShowUnlocksLimitModal = createAction<boolean>(`${appKey}/setShowUnlocksLimitModal`);
export const setShowSubscriptionModal = createAction<boolean>(`${appKey}/setShowSubscriptionModal`);
export const setSelectedFilters = createAction<CvFiltersEnumObject>(`${appKey}/setSelectedFilters`);
export const setFilterErrors = createAction<CvFiltersEnumObject>(`${appKey}/setFilterErrors`);
export const setIsCurrentPageResetFromFilters = createAction<boolean>(`${appKey}/setIsCurrentPageResetFromFilters`);
export const setIsLoadingList = createAction<boolean>(`${appKey}/setIsLoadingList`);


const initialState: CvSearchStoreInterface = {
    isLoadingList: false,
    isLoadingProfileDetails: false,
    isLoadingFavorites: false,
    isLoadingAction: false,
    isLoadingFavoritesAction: false,
    isLoadingReportAction: false,
    isFiltersMenuVisible: window?.innerWidth <= 780 ? false : true,
    isChatView: false,
    isFiltersReset: false,
    selectedProfile: null,
    searchInputs: null,
    currentPage: 1, // cv list
    currentPageFavorites: 1,
    totalCount: null,
    viewsRemaining: null,
    cvsList: [],
    favoritesList: [],
    filters: {},
    showFairUsageModal: false,
    showUnlocksLimitModal: false,
    showSubscriptionModal: false,
    selectedFilters: {},
    filterErrors: {},
    newFavorites: [],
    isCurrentPageResetFromFilters: false
};

export const appCvSearchSlice = createSlice({
    name: appKey,
    initialState: initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(searchCvs.pending, (state, action) => {
            state.isLoadingList = true
        })
        builder.addCase(searchCvs.fulfilled, (state, action) => {
            state.isLoadingList = false;
            const res = action?.payload;
            if (res?.profiles) {
                state.cvsList = res?.profiles;
                state.totalCount = res?.total;
                if (state.viewsRemaining === null || res?.fromCache === false) {
                    state.viewsRemaining = res?.viewsRemaining;
                }
            }
        });
        builder.addCase(searchCvs.rejected, (state, action) => {
            state.isLoadingList = false;
            const errorMsg = action.error?.message;
            if (errorMsg === 'Forbidden') {
                Toast.Error({ content: t('not_allowed_to_view') })
            }
        })
        builder.addCase(prefetchNextCvsPageIfNeeded.fulfilled, (state, action) => {
            const res = action?.payload;
            if (res?.fromCache === false) {
                state.viewsRemaining = res?.viewsRemaining;
            }
        });
        builder.addCase(setCvSearchInputs, (state, action) => {
            state.searchInputs = action.payload;
        })
        builder.addCase(setIsFiltersMenuVisible, (state, action) => {
            state.isFiltersMenuVisible = action.payload;
        })
        builder.addCase(setSelectedProfile, (state, action) => {
            state.selectedProfile = action.payload;
        })

        builder.addCase(getCvProfileDetails.pending, (state, action) => {
            state.isLoadingProfileDetails = true
        })
        builder.addCase(getCvProfileDetails.fulfilled, (state, action) => {
            state.isLoadingProfileDetails = false;
            const profileDetails = action?.payload;
            if (profileDetails) {
                state.viewsRemaining = state.viewsRemaining - 1;
                state.selectedProfile = profileDetails;
                state.cvsList = updateObjectInList(state.cvsList, profileDetails.externalId, { ...profileDetails, canViewDetails: true });
                invalidateApiResponseInCache(listCvsUrl, getInvalidationParams(state));
            }
        });
        builder.addCase(getCvProfileDetails.rejected, (state, action) => {
            state.isLoadingProfileDetails = false;
            Toast.Error({ content: action.error?.message })
        })

        builder.addCase(unlockCvProfile.pending, (state, action) => {
            state.isLoadingAction = true
        })
        builder.addCase(unlockCvProfile.fulfilled, (state, action) => {
            state.isLoadingAction = false;

            const profileUpdates = action?.payload;
            const selectedProfile = state.selectedProfile;
            if (profileUpdates && selectedProfile) {
                const updatesObj = {
                    ...selectedProfile,
                    isUnlocked: true,
                    lastName: profileUpdates.lastName,
                    email: profileUpdates.email,
                    mobile: profileUpdates.mobile,
                    pdfCvUrl: profileUpdates.pdfCvUrl,
                    videoCv: selectedProfile.videoCv?.map(v => { return { ...v, url: profileUpdates.videoCvUrl } }),
                    links: profileUpdates.links
                }
                state.selectedProfile = {
                    ...updatesObj
                };
                state.cvsList = updateObjectInList(state.cvsList, selectedProfile.externalId, updatesObj);
                invalidateApiResponseInCache(listCvsUrl, getInvalidationParams(state));
            }
            Toast.Success({ content: t(`profile_unlocked`) });
        })
        builder.addCase(unlockCvProfile.rejected, (state, action) => {
            state.isLoadingAction = false;
            Toast.Error({ content: action.error?.message })
        })

        builder.addCase(getCvFavoriteProfiles.pending, (state, action) => {
            state.isLoadingFavorites = true;
        })
        builder.addCase(getCvFavoriteProfiles.fulfilled, (state, action) => {
            state.isLoadingFavorites = false;
            const res = action?.payload;
            if (res?.profiles) {
                state.favoritesList = res?.profiles;
                state.totalCount = res?.total;
                state.viewsRemaining = res?.viewsRemaining;
                state.newFavorites = [];
            }
        });
        builder.addCase(getCvFavoriteProfiles.rejected, (state, action) => {
            state.isLoadingFavorites = false;
            Toast.Error({ content: action.error?.message })
        })

        builder.addCase(addCvProfileToFavorites.pending, (state, action) => {
            state.isLoadingFavoritesAction = true;
            const selectedProfile = state.selectedProfile;
            state.selectedProfile = {
                ...selectedProfile,
                isFavorite: true
            };
            state.cvsList = updateObjectInList(state.cvsList, selectedProfile.externalId, { isFavorite: true });
            const newFavoritesArray = [...state.newFavorites];
            newFavoritesArray.push(selectedProfile.externalId);
            state.newFavorites = [...newFavoritesArray];
            invalidateApiResponseInCache(listCvsUrl, getInvalidationParams(state));
            Toast.Success({ content: t('profile_added_to_favorites') });
        })
        builder.addCase(addCvProfileToFavorites.fulfilled, (state, action) => {
            state.isLoadingFavoritesAction = false;
            // not used since we do optimistic update
            // const selectedProfile = state.selectedProfile;
            // state.selectedProfile = {...selectedProfile,isFavorite: true }
        });
        builder.addCase(addCvProfileToFavorites.rejected, (state, action) => {
            state.isLoadingFavoritesAction = false;
            Toast.Error({ content: action.error?.message });
            const selectedProfile = state.selectedProfile;
            if (selectedProfile) {
                state.selectedProfile = {
                    ...selectedProfile,
                    isFavorite: false
                }
                state.cvsList = updateObjectInList(state.cvsList, selectedProfile.externalId, { isFavorite: false });
                let newFavoritesArray = [...state.newFavorites];
                newFavoritesArray = newFavoritesArray.filter(f => f !== selectedProfile.externalId);
                state.newFavorites = [...newFavoritesArray];
                invalidateApiResponseInCache(listCvsUrl, getInvalidationParams(state));
            }
        })

        builder.addCase(removeCvProfileFromFavorites.pending, (state, action) => {
            state.isLoadingFavoritesAction = true;
            const selectedProfile = state.selectedProfile;
            if (selectedProfile) {
                state.selectedProfile = {
                    ...selectedProfile,
                    isFavorite: false
                };
                state.cvsList = updateObjectInList(state.cvsList, selectedProfile.externalId, { isFavorite: false });
                let newFavoritesArray = [...state.newFavorites];
                newFavoritesArray = newFavoritesArray.filter(f => f !== selectedProfile.externalId);
                state.newFavorites = [...newFavoritesArray];
                invalidateApiResponseInCache(listCvsUrl, getInvalidationParams(state));
            }
            Toast.Success({ content: t('profile_removed_from_favorites') });
        })
        builder.addCase(removeCvProfileFromFavorites.fulfilled, (state, action) => {
            state.isLoadingFavoritesAction = false;
        });
        builder.addCase(removeCvProfileFromFavorites.rejected, (state, action) => {
            state.isLoadingFavoritesAction = false;
            Toast.Error({ content: action.error?.message });
            const selectedProfile = state.selectedProfile;
            if (selectedProfile) {
                state.cvsList = updateObjectInList(state.cvsList, selectedProfile.externalId, { isFavorite: true });
                const newFavoritesArray = [...state.newFavorites];
                newFavoritesArray.push(selectedProfile.externalId);
                state.newFavorites = [...newFavoritesArray];
                invalidateApiResponseInCache(listCvsUrl, getInvalidationParams(state));
            }
        })

        builder.addCase(reportCvProfile.pending, (state, action) => {
            state.isLoadingReportAction = true
        })
        builder.addCase(reportCvProfile.fulfilled, (state, action) => {
            state.isLoadingReportAction = false;
        });
        builder.addCase(reportCvProfile.rejected, (state, action) => {
            state.isLoadingReportAction = false;
            Toast.Error({ content: action.error?.message })
        })
        builder.addCase(setCurrentPage, (state, action) => {
            state.currentPage = action.payload;
        })
        builder.addCase(setCurrentFavoritesPage, (state, action) => {
            state.currentPageFavorites = action.payload;
        })
        builder.addCase(setIsChatView, (state, action) => {
            state.isChatView = action.payload;
        })
        builder.addCase(setCvSearchFilters, (state, action) => {
            state.filters = action.payload;
        })
        builder.addCase(setIsFiltersReset, (state, action) => {
            state.isFiltersReset = action.payload;
        })
        builder.addCase(setShowFairUsageModal, (state, action) => {
            state.showFairUsageModal = action.payload;
        })
        builder.addCase(setShowUnlocksLimitModal, (state, action) => {
            state.showUnlocksLimitModal = action.payload;
        })
        builder.addCase(setShowSubscriptionModal, (state, action) => {
            state.showSubscriptionModal = action.payload;
        })
        builder.addCase(setSelectedFilters, (state, action) => {
            state.selectedFilters = action.payload;
        })
        builder.addCase(setFilterErrors, (state, action) => {
            state.filterErrors = action.payload;
        })
        builder.addCase(setIsCurrentPageResetFromFilters, (state, action) => {
            state.isCurrentPageResetFromFilters = action.payload;
        })
        builder.addCase(setIsLoadingList, (state, action) => {
            state.isLoadingList = action.payload;
        })


    }
})

export default appCvSearchSlice.reducer
